import React, {useState} from 'react';
import User from './User';
import Box from '../Common/Box';
import {observer} from 'mobx-react';
import PersonAdd from '@mui/icons-material/PersonAdd';
import AnonUserDialog from './AnonUserDialog';
import utils, {createInviteLink} from '../lib/utils';
import {notifier} from '../lib/Notifier';
import config from '../config';
import queryString from "query-string";
import {SublistUser} from "./SublistUser";
import ContentCopy from "@mui/icons-material/ContentCopy";
import RoundButton from "../Common/RoundButton";
import shadowClient from "../lib/clients/shadow/shadowClient";
import {createScrollbarStyle, getPanelColors} from "../lib/theme/getShadowTheme";
import {useTheme} from "@mui/material/styles";
import {SxProps} from "@mui/material";
import ControlButton from "../Common/ControlButton";
import RoomPreferencesRounded from "@mui/icons-material/RoomPreferencesRounded";
import RoomSettings from "./RoomSettings";
import {RoomTypes} from "../lib/clients/shadow/interfaces";

const makeStyles = () => ({
    userType: {
        textTransform: "uppercase",
        fontWeight: "bold",
        fontSize: "12px",
        padding: "16px",
        opacity: 0.7
    },
    hidden: {
        display: "none"
    }
});

type UserListProps = {
    visible?: boolean
    sx?: SxProps
};

//@todo clean this component
function UserList(
        {
            visible = true,
            sx,
        }: UserListProps) {


    const theme = useTheme();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [roomSettingsVisible, setRoomSettingsVisible] = useState(false);

    if (!visible) {
        return null;
    }

    const clientUser = shadowClient.getClientUser();
    const room = shadowClient.roomStore.get(clientUser?.room_id);

    if (!room) {
        // return <LoadingView expand message={"No users"}/>
        return null;
    }

    const users = shadowClient.userStore.getUsersInRoom(room.id);
    const onlineUsers = users.filter(user => user.online_status === "online");
    const offlineUsers = users.filter(user => user.online_status !== "online");
    const joinedUserIds = [...onlineUsers, ...offlineUsers].map(u => u.id);
    const invitedUsers = shadowClient.userStore.users.filter(user => room.invited.includes(user.id));
    const visibleInvitedUsers = invitedUsers.filter(user => !joinedUserIds.includes(user.id));
    const bannedUsers = shadowClient.userStore.users.filter(user => room.banned.includes(user.id));

    const ownerId = room['owner_id'];
    const isOwner = clientUser && ownerId === clientUser.id;
    const isPublic = room.type === RoomTypes.PUBLIC;
    const isAdmin = clientUser && clientUser.isAdmin_();
    const withSettingsButton = isOwner || isAdmin;

    const closeDialog = () => {
        setDialogVisible(false);
    }

    const styles = makeStyles();

    const {background: backgroundColor, color: panelColor} = getPanelColors(theme);
    let style = createScrollbarStyle(backgroundColor);

    style = {
        ...style,
        background: backgroundColor,
        ...sx,
    }

    return (
            <Box
                    sx={{
                        color: panelColor,
                        borderLeft: theme.palette.mode === "dark" ? "initials" : "solid 1px #d6dbe1",
                        overflow: "auto",
                        height: "100%",
                        flex: 1,
                        fontSize: "14px",
                        ...style
                    }}

            >
                <AnonUserDialog room={room} open={dialogVisible} onClose={closeDialog}/>

                <Box alignItems={"center"}
                     row
                     sx={{
                         display: "flex",
                         width: "100%",
                         alignItems: "center",
                         textTransform: "uppercase",
                         padding: "0px 16px",
                         height: "53px",
                         fontWeight: 800
                     }}>
                    <div style={{
                        maxWidth: withSettingsButton ? "150px" : "200px",
                        overflowX: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}>
                        {room.name}
                    </div>

                    {withSettingsButton && <ControlButton
                            label={"Room settings"}
                            color={"red"}
                            sx={{marginLeft: "4px"}}
                            onClick={() => {
                                setRoomSettingsVisible(true);
                            }}
                            icon={RoomPreferencesRounded}
                    />}

                    <RoomSettings
                            open={roomSettingsVisible}
                            onClose={() => setRoomSettingsVisible(false)}
                    />

                    {isPublic && <ControlButton
                            label={"Invite"}
                            color={"red"}
                            sx={{marginLeft: "4px"}}
                            onClick={() => {
                                // setDialogVisible(true);
                                const inviteLink = createInviteLink(room, clientUser.login_token);
                                utils.copyToClipboard(inviteLink).then(() => {
                                    notifier.success("Successfully copied an invite link to the clipboard.");
                                }).catch(notifier.error);
                            }}
                            icon={PersonAdd}
                    />}
                </Box>

                {isOwner && <div>
                    <Box sx={{
                        ...styles.userType,
                        ...visibleInvitedUsers.length === 0 && styles.hidden
                    }}>
                        Invited
                    </Box>
                    {visibleInvitedUsers.map((user, i) =>
                            <SublistUser
                                    key={i}
                                    user={user}
                                    onRemoveText={"Cancel invite"}
                                    onRemove={async () => {
                                        await shadowClient.patchRoom(room.id, {
                                            invited: room.invited.filter(userId => userId !== user.id)
                                        })
                                    }}
                            >
                                <RoundButton
                                        width={24}
                                        icon={ContentCopy}
                                        label={"Copy invite link"}
                                        hoverColor={theme.palette.mode === "dark" ? "" : "#e5e5e5"}
                                        onClick={() => {
                                            const inviteLink = config.appUrl + `/rooms/${room.id}?` + queryString.stringify({
                                                token: user.login_token,
                                            });
                                            utils.copyToClipboard(inviteLink).then(() => {
                                                notifier.success("Successfully copied an invite link to the clipboard.");
                                            }).catch(notifier.error)
                                        }}
                                        sx={{
                                            marginRight: "5px"
                                        }}
                                />
                            </SublistUser>
                    )}
                </div>}

                {isOwner && <div>
                    <Box
                            sx={{
                                ...styles.userType,
                                ...bannedUsers.length === 0 && styles.hidden
                            }}>
                        Banned
                    </Box>
                    {bannedUsers.map((user, i) =>
                            <SublistUser
                                    key={i}
                                    user={user}
                                    onRemoveText={"Unban"}
                                    onRemove={async () => {

                                        await shadowClient.patchRoom(room.id, {
                                            banned: room.banned.filter(userId => userId !== user.id)
                                        })
                                    }}
                            />
                    )}
                </div>}

                <div>
                    <Box
                            sx={{
                                ...styles.userType,
                                ...onlineUsers.length === 0 && styles.hidden
                            }}>
                        {"Online - " + onlineUsers.length}
                    </Box>
                    {onlineUsers.map((user, i) =>
                            <User
                                    key={i}
                                    user={user}
                                    owner={ownerId === user.id}
                            />
                    )}
                </div>

                <div>
                    <Box sx={{
                        ...styles.userType,
                        ...offlineUsers.length === 0 && styles.hidden
                    }}>
                        {"Offline - " + offlineUsers.length}
                    </Box>
                    {offlineUsers.map((user, i) =>
                            <User
                                    key={i}
                                    user={user}
                                    owner={ownerId === user.id}
                            />
                    )}
                </div>
            </Box>
    );
}

export default observer(UserList);
